import { useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createAppTheme, resolveThemeMode } from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ReportProvider } from './contexts/ReportContext';
import { ShareProvider } from './contexts/ShareContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { RealEstateProvider } from './contexts/RealEstateContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import { setApiErrorNotifier, settingsAPI } from './services/api';
import MainLayout from './components/Layout/MainLayout';
import SharedReportView from './pages/Shared/SharedReportView';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import VerifyEmail from './pages/Auth/VerifyEmail';
import routeConfig from './config/routeConfig';

const ApiErrorNotifierBridge = () => {
  const { showError } = useNotification();

  useEffect(() => {
    setApiErrorNotifier(showError);
    return () => setApiErrorNotifier(null);
  }, [showError]);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (!reason || typeof reason !== 'object') {
        return;
      }

      if (!('isNotified' in reason)) {
        return;
      }

      if (!reason.isNotified && reason.message) {
        showError(reason.message);
      }

      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [showError]);

  return null;
};

// Loading component
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    }}
  >
    <CircularProgress />
  </Box>
);

// Protected routes wrapper
const ProtectedRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PermissionProvider>
      <ProjectProvider>
        <ReportProvider>
          <ShareProvider>
            <RealEstateProvider>
              <MainLayout>
                <Routes>
                  {routeConfig.map((route) => {
                    const Element = route.element;
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<Element />}
                      />
                    );
                  })}
                </Routes>
              </MainLayout>
            </RealEstateProvider>
          </ShareProvider>
        </ReportProvider>
      </ProjectProvider>
    </PermissionProvider>
  );
};

// Auth route wrapper (redirect if already logged in)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ThemeSettingsSyncBridge = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { setThemePreference } = useThemeMode();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    let isActive = true;

    const loadThemePreference = async () => {
      try {
        const response = await settingsAPI.get();
        if (isActive && response?.theme) {
          setThemePreference(response.theme);
        }
      } catch {
        // Keep the current local preference if settings fetch fails.
      }
    };

    loadThemePreference();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, isLoading, setThemePreference]);

  return null;
};

const AppContent = () => {
  const { themePreference } = useThemeMode();
  const [systemMode, setSystemMode] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = (event) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }

    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  const activeMode = resolveThemeMode(themePreference, systemMode);
  const theme = useMemo(() => createAppTheme(activeMode), [activeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <ApiErrorNotifierBridge />
        <BrowserRouter>
          <AuthProvider>
            <ThemeSettingsSyncBridge />
            <Routes>
              <Route path="/shared/:token" element={<SharedReportView />} />
              <Route
                path="/login"
                element={
                  <AuthRoute>
                    <Login />
                  </AuthRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <AuthRoute>
                    <Signup />
                  </AuthRoute>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <AuthRoute>
                    <VerifyEmail />
                  </AuthRoute>
                }
              />
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
